import { getFullName } from '@the-platform-group/formatters/people'
import { formatStringAddress } from 'transforms/address'
import { stringifyJob } from '@the-platform-group/formatters/jobFunctions'
import Rollbar from 'utils/rollbar'
import { snakeCase } from 'change-case-object'

/**
 * Helper to get primary job from person
 * @param {Object} person - Person object with jobs array
 * @returns {Object|undefined} - Primary job or undefined
 */
export const getPrimaryJob = person => person?.jobs?.find(job => job.isPrimary)

/**
 * Unified field value extraction for person/profile objects
 * Supports both person objects and review API response data
 * @param {Object} params - Function parameters
 * @param {Object} params.person - Person object or review data
 * @param {Object} params.preview - Preview data containing counts (optional for person objects)
 * @param {string} params.fieldKey - The field key to extract
 * @param {boolean} params.isSource - Whether this is a source person (for preview data)
 * @param {boolean} params.fromReview - Whether data is from review API response (default: false)
 * @returns {string} - The field value or '-'
 */
export function getProfileFieldValue({ person, preview, fieldKey, isSource, fromReview = false }) {
  if (!person) {
    return '-'
  }

  // Prepare data sources based on format
  const primaryJob = fromReview ? person.primaryJob : getPrimaryJob(person)
  const counts = fromReview ? preview?.meta?.review?.counts || {} : null
  const previewData =
    !fromReview && preview ? (isSource ? preview.sourcePerson : preview.targetPerson) : null
  const socialProfiles = fromReview ? person.personSocialProfiles || [] : null
  const address = primaryJob?.address || primaryJob?.organization?.address

  let fieldValue

  switch (fieldKey) {
    case 'name':
      fieldValue = getFullName(person)
      break

    case 'email':
      fieldValue = primaryJob?.email
      break

    case 'phone':
      fieldValue = primaryJob?.phone || primaryJob?.mobilePhone
      break

    case 'organization':
      fieldValue = primaryJob?.organization?.name
      break

    case 'address':
      fieldValue = formatStringAddress(address)
      break

    case 'jobFunction':
      fieldValue = stringifyJob(snakeCase(primaryJob?.jobFunctions), '', message =>
        Rollbar.error(message),
      )
      break

    case 'customFirstName':
      fieldValue = counts?.customFirstNameCount ?? previewData?.customFirstNameCount
      break

    case 'customEmail':
      fieldValue = counts?.customEmailCount ?? previewData?.customEmailCount
      break

    case 'customPhone':
      fieldValue = counts?.customPhoneCount ?? previewData?.customPhoneCount
      break

    case 'customField':
      fieldValue = counts?.customFieldCount ?? previewData?.customFieldCount
      break

    case 'activity':
      fieldValue = counts?.activityCount ?? previewData?.activityCount
      break

    case 'tagging':
      fieldValue = counts?.taggingCount ?? previewData?.taggingCount
      break

    case 'note':
      fieldValue = counts?.noteCount ?? previewData?.noteCount
      break

    case 'internalNote':
      fieldValue = counts?.internalNoteCount ?? previewData?.internalNoteCount
      break

    case 'task':
      fieldValue = counts?.taskCount ?? previewData?.taskCount
      break

    case 'surveyResponse':
      fieldValue = counts?.surveyResponseCount ?? previewData?.surveyResponseCount
      break

    case 'customerFavoritePeople':
      fieldValue = counts?.customerFavoritePeopleCount ?? previewData?.customerFavoritePeopleCount
      break

    case 'linkedInUrl':
      fieldValue = socialProfiles?.find(p => p.provider === 'linked_in')?.url
      break

    case 'postmarkSuppression':
      fieldValue = counts?.postmarkSuppressionCount ?? previewData?.postmarkSuppressionCount
      break

    default:
      break
  }

  return fieldValue || '-'
}

export function getProfileSummary(person) {
  if (!person) {
    return null
  }

  const primaryJob = getPrimaryJob(person)
  const fullName = getFullName(person)
  const jobTitle = getJobFunctionLabel(primaryJob)
  const organizationName = primaryJob?.organization?.name

  return {
    fullName,
    jobTitle,
    organizationName,
  }
}

export const getJobFunctionLabel = job => {
  return stringifyJob(snakeCase(job?.jobFunctions), '', message => Rollbar.error(message))
}

export function getJobTitleAndOrgName(job) {
  if (!job) {
    return '-'
  }
  const title = getJobFunctionLabel(job)
  const orgName = job.organization?.name
  return `${title ? `${title}, ` : ''} ${orgName || ''}`.trim()
}

export function sortJobsAccordingToPrimary(jobs) {
  return jobs?.sort((a, b) => (a?.isPrimary ? -1 : 1)) || []
}

/**
 * Transforms frontend job mappings state to API format
 * Frontend: { [sourceJobId]: { targetId, overrideValues: { email, phone, active } } }
 * API: { job_mappings: { sourceJobId: targetJobId }, job_overrides: [{ job_id, email, phone, active }] }
 */
export function transformJobMappingsForAPI(jobMappings) {
  const apiJobMappings = {}
  const apiJobOverrides = []
  const overridesByTargetJob = {}

  Object.entries(jobMappings).forEach(([sourceJobId, mapping]) => {
    const { targetId, overrideValues } = mapping

    // Build job_mappings: source -> target (or null if no target)
    apiJobMappings[sourceJobId] = targetId || null

    // Collect overrides by target job (multiple source jobs may map to same target)
    if (targetId && overrideValues && Object.keys(overrideValues).length > 0) {
      if (!overridesByTargetJob[targetId]) {
        overridesByTargetJob[targetId] = {}
      }
      // Merge override values - later mappings will override earlier ones
      Object.assign(overridesByTargetJob[targetId], overrideValues)
    }
  })

  // Build job_overrides array from collected overrides
  Object.entries(overridesByTargetJob).forEach(([targetJobId, overrides]) => {
    const apiJobOverride = { job_id: targetJobId }
    if (overrides.email) {
      apiJobOverride.email = overrides.email
    }
    if (overrides.phone) {
      apiJobOverride.phone = overrides.phone
    }
    if (overrides.active !== undefined) {
      // Convert "Active"/"Inactive" string to boolean for API
      apiJobOverride.active = overrides.active === 'Active'
    }
    if (Object.keys(apiJobOverride).length > 1) {
      apiJobOverrides.push(apiJobOverride)
    }
  })

  return {
    jobMappings: apiJobMappings,
    jobOverrides: apiJobOverrides,
  }
}

/**
 * Unified field value extraction for job objects
 * Supports job fields and optional preview data fields
 * @param {Object} job - The job object
 * @param {string} fieldKey - The field key to extract
 * @param {Object} options - Optional configuration
 * @param {Object} options.preview - Preview data containing counts
 * @param {boolean} options.isSource - Whether this is a source job (for preview data)
 * @param {string|null} options.defaultValue - Default value when field is empty (default: '-')
 * @returns {string|null} - The field value or default value
 */
export function getJobFieldValue(job, fieldKey, options = {}) {
  const { preview = null, isSource = false, defaultValue = '-' } = options

  if (!job) {
    return defaultValue
  }

  const previewData = preview ? (isSource ? preview.sourcePerson : preview.targetPerson) : null

  let fieldValue

  switch (fieldKey) {
    case 'email':
      fieldValue = job.email
      break
    case 'phone':
      fieldValue = job.phone || job.mobilePhone
      break
    case 'active':
      if (job.active === true) {
        fieldValue = 'Active'
      } else if (job.active === false) {
        fieldValue = 'Inactive'
      }
      break
    case 'organization':
      fieldValue = job.organization?.name
      break
    case 'address':
      fieldValue = formatStringAddress(job.address || job.organization?.address)
      break
    case 'activities':
      fieldValue = previewData?.activityCount
      break
    default:
      break
  }

  return fieldValue ?? defaultValue
}

/**
 * Gets the override value for a specific field from mapped source jobs
 * @param {Array} mappedSourceJobs - Array of source jobs with overrideValues
 * @param {string} fieldKey - The field to get override for
 * @returns {Object|null} - { value, sourceJob } or null if no override
 */
export const getJobOverrideValue = (mappedSourceJobs, fieldKey) => {
  const overriddenSourceJob = mappedSourceJobs.find(
    sourceJob => sourceJob?.overrideValues?.[fieldKey] !== undefined,
  )

  if (overriddenSourceJob) {
    const overrideValue = overriddenSourceJob.overrideValues[fieldKey]
    if (overrideValue && overrideValue !== 'factset') {
      return {
        value: overrideValue,
        sourceJob: overriddenSourceJob,
      }
    }
  }

  return null
}
